// CDP integration — funnel instrumentation through the platform's own customer data
// platform (the `cdp` repository, client `@cppv3/cdp-browser`), running in parallel to
// SEGMENT / KLAVIYO / GTM and changing nothing they send.
//
// It only listens. It subscribes to the same bus channels the other integrations do
// and to DOM clicks, reads pipe state, and never writes a pipe variable, a DOM node or
// a bus payload: payloads are shared objects (Segment's subscriber assigns
// `payload.clickName` on the one Klaviyo also receives), so everything sent here is
// built from fields picked out of them, never from the payload itself.
//
// Every event carries the pipe context (pipe / distribution / release, language,
// geography, currency, ingress ids, the page and its funnel markers, the variables),
// because the CDP's BigQuery table keeps only `properties`, `traits` and `context` as
// JSON — a field not in `properties` never reaches the warehouse. The event contract
// and the BigQuery model built on it are docs/architecture/cdp-instrumentation.md.
//
// Only type imports from aliases: the co-located unit test runs under bare
// `node --test`, which cannot resolve `@render/*` or the client package at runtime.
// The client and the consent reader are loaded with `import()` on the paths that
// need them.

import type { PipeIntegration, PipeIntegrationDef } from '../index'
import type {
  ActionEndPayload,
  ActionRunPayload,
  NavigationEventPayload,
  NavigationRequestPayload,
  PIPE_EVENTS,
  PipeDisplayMode,
  PipeInterface,
  VariableSetPayload,
} from '@render/composables/usePipe'
import type {
  OrderCreatedPayload,
  PAYMENT_EVENTS,
  PaymentEventPayload,
} from '@render/composables/usePaymentEvents'
import type { Analytics, HighEntropyHint } from '@cppv3/cdp-browser'
// With its extension: the co-located test loads this module under bare `node --test`.
import { CDP_HOSTS } from './hosts.ts'

/** The integration row's config, validated by `CdpIntegrationConfigDto` on the API. */
export interface CdpConfig {
  writeKey?: string
  /** Edge origin override. Absent → the edge of the build's `APP_ENV`. */
  host?: string
  /** Variable keys (`global.gender`, `pipe.answer1`) never sent, on top of the built-in PII list. */
  excludeVariables?: string[]
  /** Hold every event until cookie consent is granted, where `global.cookieConsentEnabled` is on. */
  honourCookieConsent?: boolean
  disableClickTracking?: boolean
  debug?: boolean
}

/**
 * Bumped when a field changes meaning, so the BigQuery model can branch on it.
 * 2: `pageLoadId` on every event; `navType` and `isNewLoad` on page views; and
 * `previousPageDurationMs` only between two pages of the same load.
 * 3: one event per click — `Answer Given`, the page view it led to (`via…`), or
 * `Component Clicked` with an `outcome`; `handlerId` on clicks; option roles and authored
 * click actions count as something to click, so `element` is no longer empty on them.
 * 4: `suppressed` (`suppressedCounts`), so its absence means nothing was folded; a click
 * waits for the runs of its own actions rather than up to 15 s for a page.
 * 5: `partId` on a click and `viaPartId` on the page view it led to: the item of the
 * reacting component that was clicked (an option, a plan card), so its absence means the
 * click was on no item.
 * 6: `Lead Created`, with the `orderId` Pipeorders gave it, whenever this browser creates
 * the visitor's order, so a lead without one is a lead the browser did not send.
 * 7: a press the browser reports no click for is a click too: a CTA tapped on a touch screen
 * (it acts on the pointerup and cancels the touch's click), an option or plan card chosen
 * from the keyboard. Before 7 such a press sent nothing, and the page it led to named no
 * click, so a CTA's clicks fell far short of the moves it made.
 * 8: `device` on every event: the screen and viewport in CSS pixels, the pixel ratio, touch
 * points and time zone (`deviceProperties`); and the phone's model and OS version among the
 * client hints in `context.userAgentData` (`CLIENT_HINTS`), where the browser has them. From
 * 8 on, an event without `device` comes from a page that could read none of it.
 * 9: `device.textScale`, the text size the visitor's device asks pages for against its default
 * (`textScaleOf`); page zoom never counts. From 9 on, an event without it comes from a browser
 * that does not tell.
 */
export const CDP_SCHEMA_VERSION = 9

/**
 * The client hints the client asks the browser for. Only Chromium has any; Chrome on
 * Android names no model in its user agent ("Android 10; K") and gives it only here.
 */
export const CLIENT_HINTS: HighEntropyHint[] = ['model', 'platformVersion']

type PaymentChannels = typeof PAYMENT_EVENTS
type PaymentChannel = PaymentChannels[keyof PaymentChannels]

// Channel names are spelled out rather than value-imported (see the header); each is
// pinned to the literal type of its constant so a rename fails type-check.
const RESET_USER_EVENT: PaymentChannels['resetUser'] = 'user:reset'
const ORDER_CREATED_EVENT: PaymentChannels['orderCreated'] = 'order:created'
const VARIABLE_SET_EVENT: (typeof PIPE_EVENTS)['variableSet'] = 'variable:set'
const ACTION_START_EVENT: (typeof PIPE_EVENTS)['actionStart'] = 'action:start'
const ACTION_END_EVENT: (typeof PIPE_EVENTS)['actionEnd'] = 'action:end'
const NAVIGATION_REQUEST_EVENT: (typeof PIPE_EVENTS)['navigationRequest'] = 'navigation:request'
const CUSTOM_EVENT: PaymentChannels['trackingCustomEvent'] = 'tracking:custom-event'
const STATIC_MODE = 'static' as PipeDisplayMode.STATIC

/**
 * The provider-agnostic payment lifecycle (the channels GTM consumes), one CDP event
 * each. The `tracking:checkout-*` channels are deliberately absent: they duplicate these
 * for Segment/Klaviyo, and subscribing to both would count every checkout twice.
 */
export const PAYMENT_LIFECYCLE: ReadonlyArray<{ channel: PaymentChannel; event: string }> = [
  {
    channel: 'payment:plan-selected' satisfies PaymentChannels['planSelected'],
    event: 'Plan Selected',
  },
  {
    channel: 'payment:checkout-visited' satisfies PaymentChannels['checkoutVisited'],
    event: 'Checkout Visited',
  },
  {
    channel: 'payment:checkout-rendered' satisfies PaymentChannels['checkoutRendered'],
    event: 'Checkout Rendered',
  },
  {
    channel: 'payment:checkout-started' satisfies PaymentChannels['checkoutStarted'],
    event: 'Checkout Started',
  },
  {
    channel: 'payment:checkout-requested' satisfies PaymentChannels['checkoutRequested'],
    event: 'Checkout Requested',
  },
  {
    channel: 'payment:checkout-loaded' satisfies PaymentChannels['checkoutLoaded'],
    event: 'Checkout Loaded',
  },
  {
    channel:
      'payment:checkout-loaded-confirmation' satisfies PaymentChannels['checkoutLoadedConfirmation'],
    event: 'Checkout Confirmation Loaded',
  },
  {
    channel: 'payment:checkout-completed' satisfies PaymentChannels['checkoutCompleted'],
    event: 'Checkout Completed',
  },
  {
    channel: 'payment:checkout-failed' satisfies PaymentChannels['checkoutFailed'],
    event: 'Checkout Failed',
  },
  {
    channel: 'payment:checkout-abandoned' satisfies PaymentChannels['checkoutAbandoned'],
    event: 'Checkout Abandoned',
  },
]

/** Groups snapshotted from variables and constants. `system.*` is sent as named fields. */
const SNAPSHOT_GROUPS = ['global', 'brand', 'pipe', 'group'] as const

const MAX_SNAPSHOT_KEYS = 80
const MAX_STRING_LENGTH = 120
// Ad names run past MAX_STRING_LENGTH (253 characters seen in October 2026) and are read by
// position, so a cut one loses its last parts. Builds before this sent them cut at 120.
const MAX_CAMPAIGN_LENGTH = 500
const MAX_CLICKS_PER_PAGE_VIEW = 100
const MAX_EVENTS_WAITING_FOR_CONSENT = 200
const MAX_COMPONENT_PATH_DEPTH = 12
const MAX_ANSWER_VALUES = 20
const MAX_ANSWERED_KEYS = 200
// A write counts as the visitor's answer only this soon after their gesture on the same
// page view; a slower one is the pipe's own bookkeeping.
const GESTURE_WINDOW_MS = 2_000
// A slider or a typed answer settles before it is sent, once per settled value.
const ANSWER_SETTLE_MS = 700
// How long a clicked component gets to react on its own — write its value, mount what it
// opens. What the click's authored actions do is waited for by their run, however long.
const CLICK_SETTLE_MS = 1_000
// A second click this soon on the same component is the same one: a label clicks its input.
const SAME_CLICK_MS = 50

/**
 * Variables never sent in the `vars` snapshot: direct identifiers, and state that is
 * sent as a named field or is too large to be a filter. `global.email` is sent as
 * `email` on purpose (the join key to orders and Klaviyo).
 */
export const EXCLUDED_VARIABLES: ReadonlySet<string> = new Set([
  'global.email',
  'global.firstName',
  'global.phone',
  'global.usersIPs',
  'global.trackingProperties',
  'global.paymentsApiCustomerId',
  'global.segmentUserId',
  'global.geoDevice',
  'global.deviceTimestamps',
  'global.visitedPageIds',
  'global.visitedPagePaths',
  'global.persistedDataLoaded',
  'global.currentDay',
])

// The same rule by name, for authored variables no list can anticipate.
const PII_NAME =
  /^(e-?mail\w*|phone\w*|mobile|tel|(first|last|full|sur|user)_?name|name|address\w*|street|zip|post_?code|postal_?code|password\w*|\w*token|iban|card_?number|birth_?date|date_?of_?birth|dob|users?_?ips?|ip|ip_?address)$/i
const CONTAINS_EMAIL = /[^@\s]+@[^@\s]+\.[^@\s]+/

type Scalar = string | number | boolean
export type Snapshot = Partial<Record<(typeof SNAPSHOT_GROUPS)[number], Record<string, Scalar>>>

/**
 * Scalar entries of a flat `group.name` map, nested by group so BigQuery reads them as
 * `$.vars.global.gender` without quoting a dotted key. Objects, arrays, empty strings
 * and any text containing an email address are skipped; long strings are cut.
 */
export function snapshotScalars(
  source: Record<string, unknown>,
  excluded: ReadonlySet<string> = new Set(),
): Snapshot {
  const out: Snapshot = {}
  let count = 0
  for (const [key, value] of Object.entries(source)) {
    if (count >= MAX_SNAPSHOT_KEYS) break
    const dot = key.indexOf('.')
    if (dot <= 0) continue
    const group = key.slice(0, dot) as (typeof SNAPSHOT_GROUPS)[number]
    const name = key.slice(dot + 1)
    if (!SNAPSHOT_GROUPS.includes(group) || !name || name.includes('.')) continue
    if (excluded.has(key) || PII_NAME.test(name)) continue

    let scalar: Scalar
    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (!trimmed || CONTAINS_EMAIL.test(trimmed)) continue
      scalar = trimmed.slice(0, MAX_STRING_LENGTH)
    } else if (typeof value === 'number') {
      if (!Number.isFinite(value)) continue
      scalar = value
    } else if (typeof value === 'boolean') {
      scalar = value
    } else {
      continue
    }
    ;(out[group] ??= {})[name] = scalar
    count++
  }
  return out
}

export interface IngressIds {
  ingressId?: number
  ruleSetId?: number
  destinationId?: number
}

const positiveInt = (raw: string | null | undefined): number | undefined => {
  if (!raw || !/^\d+$/.test(raw)) return undefined
  const n = Number(raw)
  return n > 0 ? n : undefined
}

// The ingress gateway appends `ingress_id` / `ingress_rule_set_id` / `destination_id` to
// every redirect and, where its GW_QUERY_EXTEND_PARAM is set, also folds all three into
// one param as `…_ingress_id_1_ingress_rule_set_id_3_destination_id_2_` (0 = none).
const EXTENDED_INGRESS = /_ingress_id_(\d+)_ingress_rule_set_id_(\d+)_destination_id_(\d+)_/

/** Ingress ids carried by a landing URL's query string. Never throws. */
export function readIngressIds(search: string): IngressIds {
  let params: URLSearchParams
  try {
    params = new URLSearchParams(search)
  } catch {
    return {}
  }
  const explicit: IngressIds = {
    ingressId: positiveInt(params.get('ingress_id')),
    ruleSetId: positiveInt(params.get('ingress_rule_set_id')),
    destinationId: positiveInt(params.get('destination_id')),
  }
  if (explicit.ingressId || explicit.destinationId) return prune(explicit)

  for (const value of params.values()) {
    const match = EXTENDED_INGRESS.exec(value)
    if (match) {
      return prune({
        ingressId: positiveInt(match[1]),
        ruleSetId: positiveInt(match[2]),
        destinationId: positiveInt(match[3]),
      })
    }
  }
  return {}
}

const prune = <T extends object>(value: T): T =>
  Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as T

/**
 * What this integration keeps in its own localStorage key, beside the pipe's store so
 * the landing-page reset (`clearPersistedData`) cannot wipe it — the ingress trace-id
 * pattern (@render/lib/ingressTrace). Scoped per distribution.
 *
 * `ingress` follows the most recent landing that carried ingress ids, like the current
 * trace id. `run` is one pass through the funnel: it starts when the pipe resets its
 * state (`pipe:data-cleared`, the landing page) and lasts as long as that state does,
 * so a visitor who comes back through an abandoned-cart link continues their run.
 */
export interface CdpState {
  v: 1
  ingress: IngressIds
  run: RunState
}

export interface RunState {
  id: string
  startedAt: number
  seq: number
  seenPageIds: string[]
  /** `loadId` tells a page shown in this document load from one shown before a reload. */
  lastPage?: { id: string; shownAt: number; loadId?: string }
  /** Answers given per variable key in this run, so a changed answer is told from a first one. */
  answered?: Record<string, number>
}

/**
 * How a page view relates to the one before it in the run: the first page of the run,
 * a page not seen yet, one seen before (Back, or a loop), or the same page again (a
 * reload). Sent so the model can tell the funnel's forward moves from everything else
 * without replaying each run.
 */
export type NavType = 'entry' | 'forward' | 'revisit' | 'repeat'

export function navType(
  previousPageId: string | undefined,
  pageId: string,
  seenBefore: boolean,
): NavType {
  if (!previousPageId) return 'entry'
  if (previousPageId === pageId) return 'repeat'
  return seenBefore ? 'revisit' : 'forward'
}

export const newRun = (id: string, now: number): RunState => ({
  id,
  startedAt: now,
  seq: 0,
  seenPageIds: [],
})

function readAnswered(raw: unknown): Record<string, number> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const out: Record<string, number> = {}
  for (const [key, count] of Object.entries(raw).slice(0, MAX_ANSWERED_KEYS)) {
    if (typeof count === 'number' && Number.isInteger(count) && count > 0) out[key] = count
  }
  return Object.keys(out).length ? out : undefined
}

/** Parse a stored record; anything unreadable starts over. Never throws. */
export function parseState(raw: string | null, runId: () => string, now: number): CdpState {
  try {
    const parsed = raw ? (JSON.parse(raw) as Partial<CdpState>) : null
    const run = parsed?.run
    if (parsed?.v === 1 && run && typeof run.id === 'string' && Array.isArray(run.seenPageIds)) {
      return {
        v: 1,
        ingress: prune({
          ingressId: positiveInt(String(parsed.ingress?.ingressId ?? '')),
          ruleSetId: positiveInt(String(parsed.ingress?.ruleSetId ?? '')),
          destinationId: positiveInt(String(parsed.ingress?.destinationId ?? '')),
        }),
        run: {
          id: run.id,
          startedAt: Number(run.startedAt) || now,
          seq: Number(run.seq) || 0,
          seenPageIds: run.seenPageIds.filter((id): id is string => typeof id === 'string'),
          lastPage:
            run.lastPage &&
            typeof run.lastPage.id === 'string' &&
            typeof run.lastPage.shownAt === 'number'
              ? prune({
                  id: run.lastPage.id,
                  shownAt: run.lastPage.shownAt,
                  loadId: typeof run.lastPage.loadId === 'string' ? run.lastPage.loadId : undefined,
                })
              : undefined,
          answered: readAnswered(run.answered),
        },
      }
    }
  } catch {
    // fall through
  }
  return { v: 1, ingress: {}, run: newRun(runId(), now) }
}

/** A click, reduced to the rendered component it landed on. */
export interface ClickTarget {
  componentId: string
  componentType?: string
  componentPath: string[]
  sharedNodeRefId?: string
  /**
   * The component that reacts: the one whose control was clicked, else the nearest one
   * around the click with an authored click action. An option card's inner heading is
   * `componentId`; the card is `handlerId`.
   */
  handlerId?: string
  /**
   * The item of `handlerId ?? componentId` the click landed on: an option, a plan card.
   * Items are not nodes, so the id is unique only within that component (a copied
   * component keeps its items' ids).
   */
  partId?: string
  element?: string
  text?: string
  href?: string
}

// The minimal DOM surface the resolver reads, so the test can hand it plain objects.
export interface ClickElement {
  tagName: string
  parentElement: ClickElement | null
  textContent: string | null
  getAttribute(name: string): string | null
  closest(selector: string): ClickElement | null
}

// Choice components render their options as `role="radio"` / `"checkbox"` divs, so the
// roles count as controls as much as the tags do.
const ACTIONABLE = [
  'a',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  '[role="button"]',
  '[role="link"]',
  '[role="radio"]',
  '[role="checkbox"]',
  '[role="option"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="slider"]',
  '[role="menuitem"]',
].join(',')

const shortText = (raw: string | null | undefined): string | undefined => {
  const text = raw?.replace(/\s+/g, ' ').trim()
  return text ? text.slice(0, 80) : undefined
}

/** The rendered components around an element, innermost first. */
export function componentChain(target: ClickElement | null): { id: string; type?: string }[] {
  const chain: { id: string; type?: string }[] = []
  for (
    let el: ClickElement | null = target?.closest('[data-id]') ?? null;
    el && chain.length < MAX_COMPONENT_PATH_DEPTH;
    el = el.parentElement?.closest('[data-id]') ?? null
  ) {
    const id = el.getAttribute('data-id')
    if (id) chain.push({ id, type: el.getAttribute('data-type') ?? undefined })
  }
  return chain
}

/**
 * The item of component `ownerId` around an element. A component that renders a list
 * from a field (options, plan cards) marks each item's root with `data-part`, never
 * `data-id`, so an item is no node to the editor, the agent or the component path. An
 * item of a component nested inside another's item belongs to the inner one.
 */
export function partOf(target: ClickElement, ownerId: string): string | undefined {
  let part = target.closest('[data-part]')
  for (let depth = 0; part && depth < MAX_COMPONENT_PATH_DEPTH; depth++) {
    if (part.closest('[data-id]')?.getAttribute('data-id') === ownerId)
      return part.getAttribute('data-part') || undefined
    part = part.parentElement?.closest('[data-part]') ?? null
  }
  return undefined
}

/**
 * The innermost rendered component around a click target. Every node RenderComponents
 * mounts carries `data-id` (its node id) and `data-type` on its root element, so the
 * nearest `[data-id]` ancestor is the component the visitor clicked, and the chain of
 * `[data-id]` ancestors is where it sits. Input values are never read.
 *
 * `hasAction` says whether a component has an authored click action. A click on
 * something with neither a control nor an action around it leaves `element` empty:
 * a dead click, unless the model finds that it led to an answer or a page.
 */
export function resolveClickTarget(
  target: ClickElement | null,
  hasAction: (componentId: string) => boolean = () => false,
): ClickTarget | null {
  const component = target?.closest('[data-id]') ?? null
  const componentId = component?.getAttribute('data-id')
  if (!target || !component || !componentId) return null

  const chain = componentChain(component)
  const path = chain.map((c) => c.id).reverse()

  const actionable = target.closest(ACTIONABLE)
  const tag = actionable?.tagName.toLowerCase()
  const isField = tag === 'input' || tag === 'select' || tag === 'textarea'
  const leafText =
    component.textContent && component.textContent.length <= 200 ? component.textContent : null
  const controlOwner = actionable?.closest('[data-id]')?.getAttribute('data-id') ?? undefined
  const withAction = chain.find((c) => hasAction(c.id))?.id
  const handlerId = controlOwner ?? withAction

  return prune({
    componentId,
    componentType: component.getAttribute('data-type') ?? undefined,
    componentPath: path,
    sharedNodeRefId:
      component.closest('[data-shared-internal]')?.getAttribute('data-shared-internal') ??
      undefined,
    handlerId,
    partId: partOf(target, handlerId ?? componentId),
    element: isField
      ? `${tag}:${actionable?.getAttribute('type') ?? 'text'}`
      : (tag ?? (withAction ? 'action' : undefined)),
    text: isField ? undefined : shortText(actionable?.textContent ?? leafText),
    href: tag === 'a' ? actionable?.getAttribute('href')?.slice(0, 200) : undefined,
  })
}

/**
 * What a press of component `nodeId` landed on, when a run of that component's click or submit
 * event is the press's doing and the browser may report no click for it: a CTA acts on the
 * pointerup and cancels the touch's click, and an option chosen from the keyboard has none.
 * Null for a click, which is counted as one already; for a key typed into a field, since
 * Enter there submits its form through the CTA, a click on neither; and for a press elsewhere.
 */
export function pressTarget(
  event: { type: string; target: unknown } | undefined,
  nodeId: string,
): ClickElement | null {
  if (!event || event.type === 'click') return null
  const el = event.target as ClickElement | null
  if (!el || typeof el.closest !== 'function' || isTypedText(el)) return null
  return componentChain(el).some((c) => c.id === nodeId) ? el : null
}

// ------------------------------------------------------------------------ answers

/** Where a visitor's answer came from: a component's own value field, or an authored action. */
export type AnswerSource = 'component' | 'action'

/**
 * `choice` (one picked value), `multi` (every value picked so far), `number`,
 * `boolean`, `text` (typed free text: counted, its value never sent), `cleared`.
 */
export type AnswerKind = 'choice' | 'multi' | 'number' | 'boolean' | 'text' | 'cleared'

/** What the pipe's content says its components do when clicked, read once. */
export interface ComponentActions {
  /** Variable key → components whose own fields name it (a select, a slider, an input). */
  fields: Map<string, Set<string>>
  /** Variable key → components whose click or submit actions write it (`set_data`). */
  actions: Map<string, Set<string>>
  /** Components with an authored click or submit action. */
  handlers: Set<string>
}

// The component events a visitor fires by clicking or submitting. Timer, reveal and
// checkout events also run processors, but nobody chose anything.
const GESTURE_EVENTS = ['onClick', 'onSubmit']
// Fields a component writes for itself about its state (`hasSelectionVariable`,
// `isValidVariable`), which follow an answer rather than being one.
const DERIVED_FIELD = /^(has|is)[A-Z]\w*Variable$/
const MAX_WALK_DEPTH = 60

type Json = Record<string, unknown>
const isJson = (v: unknown): v is Json => !!v && typeof v === 'object' && !Array.isArray(v)

function fieldKeys(value: unknown, into: Set<string>, depth: number): void {
  if (depth > 6) return
  if (Array.isArray(value)) {
    for (const item of value) fieldKeys(item, into, depth + 1)
    return
  }
  if (!isJson(value)) return
  if (value.type === 'variable_key' && typeof value.key === 'string' && value.key) {
    into.add(value.key)
    return
  }
  for (const [name, inner] of Object.entries(value)) {
    if (!DERIVED_FIELD.test(name)) fieldKeys(inner, into, depth + 1)
  }
}

/**
 * Every component of the pipe (pages and shared nodes) that could write a variable,
 * by its `variable_key` fields and by the `set_data` processors on its click and
 * submit events. A candidate only: an answer is recorded when such a component
 * actually writes during the visitor's gesture on it.
 */
export function indexComponentActions(content: unknown): ComponentActions {
  const writers: ComponentActions = {
    fields: new Map(),
    actions: new Map(),
    handlers: new Set(),
  }
  const add = (map: Map<string, Set<string>>, key: string, id: string) => {
    let ids = map.get(key)
    if (!ids) map.set(key, (ids = new Set()))
    ids.add(id)
  }
  const walk = (nodes: unknown, depth: number): void => {
    if (!Array.isArray(nodes) || depth > MAX_WALK_DEPTH) return
    for (const node of nodes) {
      if (!isJson(node)) continue
      const id = typeof node.nodeId === 'string' ? node.nodeId : null
      if (id) {
        const keys = new Set<string>()
        fieldKeys(node.fields, keys, 0)
        for (const key of keys) add(writers.fields, key, id)
        const events = isJson(node.events) ? node.events : {}
        for (const name of GESTURE_EVENTS) {
          const processors = Array.isArray(events[name]) ? (events[name] as unknown[]) : []
          if (processors.length) writers.handlers.add(id)
          for (const p of processors) {
            if (isJson(p) && p.type === 'set_data' && isJson(p.data)) {
              const key = p.data.key
              if (typeof key === 'string' && key) add(writers.actions, key, id)
            }
          }
        }
      }
      walk(node.children, depth + 1)
      if (isJson(node.slots)) for (const slot of Object.values(node.slots)) walk(slot, depth + 1)
    }
  }
  const root = isJson(content) ? content : {}
  for (const page of Array.isArray(root.pages) ? root.pages : []) {
    if (isJson(page) && isJson(page.content)) walk(page.content.children, 0)
  }
  for (const shared of Array.isArray(root.sharedNodes) ? root.sharedNodes : []) {
    if (isJson(shared) && isJson(shared.content)) walk(shared.content.children, 0)
  }
  return writers
}

/** A variable an answer may be recorded for: the snapshot's rule, applied to one key. */
export function isAnswerKey(key: string, excluded: ReadonlySet<string>): boolean {
  const dot = key.indexOf('.')
  if (dot <= 0) return false
  const group = key.slice(0, dot)
  const name = key.slice(dot + 1)
  if (!(SNAPSHOT_GROUPS as readonly string[]).includes(group) || !name || name.includes('.'))
    return false
  return !excluded.has(key) && !PII_NAME.test(name)
}

// The id an object-valued answer is known by: a plan picker stores the whole offer group,
// whose regular offer's ppoId names the plan.
function answerToken(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed && !CONTAINS_EMAIL.test(trimmed) ? trimmed.slice(0, MAX_STRING_LENGTH) : null
  }
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null
  if (typeof value === 'boolean') return String(value)
  if (isJson(value)) {
    if (value.type === 'offer_group_value' && isJson(value.regular))
      return answerToken(value.regular.ppoId)
    for (const k of ['value', 'id', 'key', 'code', 'ppoId']) {
      const inner = value[k]
      if (typeof inner === 'string' || typeof inner === 'number') return answerToken(inner)
    }
  }
  return null
}

// A typed number (a height typed as "180") is a number, not free text.
const NUMERIC = /^-?\d{1,7}([.,]\d{1,4})?$/

/**
 * An answer's value as it is sent: every picked value as a string, so a single choice,
 * a multi-select and a number all land in one BigQuery column. Typed free text is
 * counted without its value, and a value that holds an email address is never sent.
 * Null when there is nothing to send.
 */
export function answerPayload(
  value: unknown,
  typed: boolean,
): { answerKind: AnswerKind; answerValues?: string[] } | null {
  if (value === null || value === undefined) return { answerKind: 'cleared', answerValues: [] }
  if (Array.isArray(value)) {
    const values = value
      .map(answerToken)
      .filter((v): v is string => v !== null)
      .slice(0, MAX_ANSWER_VALUES)
    return { answerKind: 'multi', answerValues: values }
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? { answerKind: 'number', answerValues: [String(value)] } : null
  }
  if (typeof value === 'boolean') return { answerKind: 'boolean', answerValues: [String(value)] }
  if (typeof value === 'string' && typed) {
    const trimmed = value.trim()
    if (NUMERIC.test(trimmed)) return { answerKind: 'number', answerValues: [trimmed] }
    return trimmed ? { answerKind: 'text' } : null
  }
  if (typeof value === 'string' && !value.trim()) return { answerKind: 'cleared', answerValues: [] }
  const token = answerToken(value)
  return token === null ? null : { answerKind: 'choice', answerValues: [token] }
}

/** Whether keys typed into this element are free text (never sent) rather than a number. */
export function isTypedText(el: ClickElement | null): boolean {
  const tag = el?.tagName.toLowerCase()
  if (tag === 'textarea') return true
  if (el?.getAttribute('contenteditable') === 'true') return true
  if (tag !== 'input') return false
  const type = (el?.getAttribute('type') ?? 'text').toLowerCase()
  return !['number', 'range', 'checkbox', 'radio', 'date', 'button', 'submit'].includes(type)
}

const num = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined
const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined)

/**
 * The fields of a payment event worth a column. `trackingProperties` (every cookie on
 * the page) is left out on purpose: the event's own context already says where the
 * visit came from, and the cookie blob would be the largest part of every row.
 */
export function pickPaymentFields(
  payload: Partial<PaymentEventPayload> | undefined,
): Record<string, unknown> {
  if (!payload) return {}
  return prune({
    ppoId: str(payload.ppoId),
    paymentType: str(payload.paymentType),
    pipelineOfferType: str(payload.pipelineOfferType),
    isExitIntent: typeof payload.isExitIntent === 'boolean' ? payload.isExitIntent : undefined,
    defaultPlanSelected:
      typeof payload.defaultPlanSelected === 'boolean' ? payload.defaultPlanSelected : undefined,
    basePrice: num(payload.basePrice),
    basePeriod: num(payload.basePeriod),
    basePeriodUnit: str(payload.basePeriodUnit),
    trialPrice: num(payload.trialPrice),
    trialPeriod: num(payload.trialPeriod),
    trialPeriodUnit: str(payload.trialPeriodUnit),
    // `currency` on every event is the distribution's; this is the order's own.
    orderCurrency: str(payload.currency)?.toUpperCase(),
    orderId: str(payload.orderId),
    eventId: str(payload.eventId),
    failureReason: str(payload.reason),
    checkoutProvider: str(payload.checkoutProvider),
  })
}

/** The event a lead is sent as: Pipehouse's Orders vs CDP check pairs it with the order. */
export const LEAD_CREATED = 'Lead Created'

/** What a lead carries beside the base properties; null when there is no order to name. */
export function leadProperties(
  payload: Partial<OrderCreatedPayload> | undefined,
): { orderId: string } | null {
  const orderId = str(payload?.orderId)?.trim()
  return orderId ? { orderId } : null
}

// Landing query param → event property: the UTM set, and the ad ids the ingress gateway's
// channel extractor reads (`fb_ad_id` is the gateway's alias of `fb_ads_id`).
const CAMPAIGN_PARAMS: ReadonlyArray<readonly [string, string]> = [
  ['utm_source', 'utmSource'],
  ['utm_medium', 'utmMedium'],
  ['utm_campaign', 'utmCampaign'],
  ['utm_content', 'utmContent'],
  ['utm_term', 'utmTerm'],
  ['fb_ads_id', 'fbAdsId'],
  ['fb_ad_id', 'fbAdId'],
  ['fb_adset_id', 'fbAdsetId'],
  ['ga_ad_id', 'gaAdId'],
  ['ga_assetgroup_id', 'gaAssetgroupId'],
]

/** Campaign parameters of the landing, as camelCase properties. */
export function pickCampaign(query: Record<string, unknown> | undefined): Record<string, string> {
  const out: Record<string, string> = {}
  if (!query) return out
  for (const [param, property] of CAMPAIGN_PARAMS) {
    const value = query[param]
    if (typeof value === 'string' && value) out[property] = value.slice(0, MAX_CAMPAIGN_LENGTH)
  }
  return out
}

/** The globals `deviceProperties` reads; a test hands it a page of its own. */
export interface DeviceWindow {
  screen?: { width?: number; height?: number }
  innerWidth?: number
  innerHeight?: number
  devicePixelRatio?: number
  navigator?: { maxTouchPoints?: number }
}

const size = (v: unknown): number | undefined => {
  const n = num(v)
  return n !== undefined && n > 0 ? Math.round(n) : undefined
}

/**
 * The visitor's screen for `properties.device` (schema 8), read for every event since a
 * phone can turn: the screen and viewport in CSS pixels as the browser reports them (some
 * Android browsers swap the screen's sides when it turns, iOS never does), the pixel ratio,
 * touch points and the time zone; and the text scale (schema 9), read once per load
 * (`textScaleOf`). What the page cannot read is left out.
 */
export function deviceProperties(
  win: DeviceWindow,
  timeZone: string | undefined,
  textScale?: number,
): Record<string, unknown> | undefined {
  const ratio = num(win.devicePixelRatio)
  const touchPoints = num(win.navigator?.maxTouchPoints)
  const device = prune({
    screenWidth: size(win.screen?.width),
    screenHeight: size(win.screen?.height),
    viewportWidth: size(win.innerWidth),
    viewportHeight: size(win.innerHeight),
    pixelRatio: ratio !== undefined && ratio > 0 ? Math.round(ratio * 1000) / 1000 : undefined,
    touchPoints: touchPoints !== undefined && touchPoints >= 0 ? touchPoints : undefined,
    timeZone: str(timeZone)?.slice(0, MAX_STRING_LENGTH),
    textScale,
  })
  return Object.keys(device).length ? device : undefined
}

/** The browsers' default font size, which `medium` is unless the visitor changed it. */
const DEFAULT_FONT_PX = 16
/** iOS's body text at the text size it ships with, which every size is a step from. */
export const IOS_BODY_PX = 17

/** What `textScaleOf` is given: computed font sizes of a probe the page never shows. */
export interface TextScaleReading {
  /** `font-size: medium`: the browser's default font size, times any text zoom. */
  mediumPx: number
  /** `font: -apple-system-body`, where WebKit on a touch screen resolves it. */
  appleBodyPx?: number
  /** `env(preferred-text-scale)`, where the browser has it. */
  preferred?: number
  /** The page has `<meta name="text-scale" content="scale">`. */
  optedIn: boolean
  android: boolean
}

/**
 * The text size the visitor's device asks pages for, as a multiple of its default (1), to two
 * places; undefined where the browser does not tell. Each platform shows it one way, and none
 * of them is page zoom, which leaves CSS pixels alone:
 * - iOS and iPadOS: the Text Size setting, which no page follows on its own but which WebKit
 *   resolves `-apple-system-body` to, every app's web view included (17 px at the default).
 * - Chrome on Android: the phone's font size, as `env(preferred-text-scale)`, which Chrome 138
 *   and later report snapped to its steps (1.15, 1.3…) while showing pages at their own size.
 * - An app's web view on Android: the text zoom it shows every page at, which scales `medium`;
 *   the phone's font size unless the app fixes its own, which then is what the visitor sees.
 * - Desktop browsers: their own font size setting, which is `medium`.
 * Android shows the phone's choice nowhere else, so a browser with neither of its two ways (an
 * old Chrome, Firefox) tells only a size other than the default. A page that opts in to text
 * scaling has `medium` scaled by the same factor env() reports, so env() alone counts there.
 */
export function textScaleOf(reading: TextScaleReading): number | undefined {
  const { mediumPx, appleBodyPx, preferred, optedIn, android } = reading
  const scale =
    appleBodyPx !== undefined
      ? appleBodyPx / IOS_BODY_PX
      : optedIn && preferred !== undefined
        ? preferred
        : (preferred ?? 1) * (mediumPx / DEFAULT_FONT_PX)
  if (!Number.isFinite(scale) || scale <= 0) return undefined
  const rounded = Math.round(scale * 100) / 100
  if (android && preferred === undefined && appleBodyPx === undefined && rounded === 1)
    return undefined
  return rounded
}

const TEXT_PROBE = 'cdp-text-probe'

/**
 * The readings for `textScaleOf`, from elements that are never rendered: `display: none` has
 * their style computed without laying anything out, and every value is set `!important` on
 * each element, since a pipe's `*` rule reaches them all. `medium` is pinned to a sans-serif
 * family, as a monospace one has a default size of its own. `-apple-system-body` is a font
 * only WebKit knows: it replaces the inherited size there, and any other browser drops it.
 */
export function readTextScale(): number | undefined {
  const root = document.documentElement
  if (!root || typeof getComputedStyle !== 'function') return undefined
  const pin = (styles: Record<string, string>): HTMLElement => {
    const el = document.createElement(TEXT_PROBE)
    const all = { 'text-size-adjust': 'none', '-webkit-text-size-adjust': 'none', ...styles }
    for (const [property, value] of Object.entries(all))
      el.style.setProperty(property, value, 'important')
    return el
  }
  const probe = pin({
    display: 'none',
    'font-size': '100px',
    '--cdp-text-scale': 'env(preferred-text-scale)',
  })
  const medium = pin({ 'font-size': 'medium', 'font-family': 'sans-serif' })
  const apple = pin({ 'font-size': 'inherit', font: '-apple-system-body' })
  probe.append(medium, apple)
  try {
    root.append(probe)
    const px = (el: Element) => parseFloat(getComputedStyle(el).fontSize)
    // Empty where the browser has no such variable; the declaration as written without env().
    const env = getComputedStyle(probe).getPropertyValue('--cdp-text-scale').trim()
    const preferred = env ? num(Number(env)) : undefined
    const appleBodyPx = px(apple)
    const optedIn =
      document.querySelector('meta[name="text-scale" i]')?.getAttribute('content')?.trim() ===
      'scale'
    return textScaleOf({
      mediumPx: px(medium),
      appleBodyPx:
        appleBodyPx !== px(probe) && (num(navigator.maxTouchPoints) ?? 0) > 0
          ? appleBodyPx
          : undefined,
      preferred: preferred !== undefined && preferred > 0 ? preferred : undefined,
      optedIn,
      android: /Android/i.test(navigator.userAgent),
    })
  } catch {
    return undefined
  } finally {
    probe.remove()
  }
}

/** The page's time zone, as the browser resolves it; read once per load. */
const pageTimeZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

const safeRead = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

const safeWrite = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // blocked storage — the run and ingress ids degrade to this page load
  }
}

const randomId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`

type CurrentPage = NavigationEventPayload & { shownAt: number }

/** The visitor's last gesture on the page on screen, which a variable write is matched to. */
interface Gesture {
  /** A run started while it is still being dispatched is its doing. */
  event: Event
  target: ClickElement
  at: number
  page: CurrentPage
  typed: boolean
  /** The pointer pressed, so one the browser takes over for scrolling is let go. */
  pointerId?: number
  /** The click this gesture is, while it waits to learn what it led to. */
  click?: PendingClick
}

interface AnswerDraft {
  key: string
  value: unknown
  typed: boolean
  source: AnswerSource
  componentId: string
  componentType?: string
  at: number
  /** Writes of the variable this answer settled from: one per slider tick or keystroke. */
  writes: number
}

/**
 * A click waiting to learn what it led to, so it is sent once, as one event: the answer
 * it gave, the page view it led to, or else a click that changed nothing else. It waits
 * for the runs of the actions it set off and for a page one of them asked for — never on
 * a clock; only its component's own reaction has a window (`CLICK_SETTLE_MS`).
 */
interface PendingClick {
  target: ClickTarget
  /** The rendered components around it, innermost first. */
  chain: { id: string; type?: string }[]
  page: CurrentPage
  at: number
  answers: Map<string, AnswerDraft>
  /** Author-named (`tracking:custom-event`) by one of its actions. */
  clickName?: string
  /** Names its actions fired after the first, which no event carries. */
  extraNames: number
  /** The same click delivered again: a label clicking its input, a double-click's second. */
  repeats: number
  /** Sent as a click event if it leads to nothing else; false past the page's cap. */
  countable: boolean
  /** Its actions' runs still going. */
  runs: Set<string>
  /** One of its actions, or the click itself, asked for another page. */
  navigating: boolean
  /** Its component has had its window to react. */
  reacted: boolean
  timer: ReturnType<typeof setTimeout> | undefined
}

/**
 * A pointer press from its pointerup to the next pointerdown, so it is counted once: as the
 * click the browser sends for it, or from the pointerup itself when a run it set off came
 * first. A CTA acts on the pointerup, and on a touch screen the browser sends no click at all.
 */
interface Press {
  up: Event
  at: number
  page: CurrentPage
  /** The browser's click for it has arrived. */
  clicked: boolean
  click?: PendingClick
}

/** A run of authored actions in progress, and the click it is part of. */
interface ActionRun {
  eventName: string
  /** The component whose event it is; for a reemitted run, the shared node's ref. */
  nodeId: string
  click?: PendingClick
}

/**
 * The writer of a variable write made during a gesture, or null when the write is not
 * the visitor's answer: a `set_data` action whose source was clicked or whose click
 * action writes that key, else a component around the gesture whose own field names it.
 */
export function answerWriter(
  write: Pick<VariableSetPayload, 'key' | 'sourceInstanceKey'>,
  chain: { id: string; type?: string }[],
  writers: ComponentActions,
): { source: AnswerSource; componentId: string; componentType?: string } | null {
  if (write.sourceInstanceKey) {
    const nodeId = write.sourceInstanceKey.split('/').pop() ?? ''
    const inChain = chain.find((c) => c.id === nodeId)
    if (inChain || writers.actions.get(write.key)?.has(nodeId)) {
      return { source: 'action', componentId: nodeId, componentType: inChain?.type }
    }
    return null
  }
  const ids = writers.fields.get(write.key)
  const writer = ids ? chain.find((c) => ids.has(c.id)) : undefined
  return writer ? { source: 'component', componentId: writer.id, componentType: writer.type } : null
}

/** The click's own fields, as every event that carries a click sends them. */
function clickFields(target: ClickTarget): Record<string, unknown> {
  const { text, ...component } = target
  return { ...component, elementText: text }
}

/**
 * What an event stands for beyond itself, so the warehouse can see what folding saved and
 * what it hid: `clicks`, the same click delivered again (a label clicking its input, the
 * second click of a double-click that selected a word); `writes`, the further writes of
 * an answer's variable before it settled (slider ticks, keystrokes); `trackedClicks`, the
 * names a click's actions fired after the first, which no event carries. Undefined when
 * nothing was folded, so a schema-4 event without it folded nothing.
 */
export function suppressedCounts(counts: {
  clicks?: number
  writes?: number
  trackedClicks?: number
}): Record<string, number> | undefined {
  const kept = Object.entries(counts).filter(([, n]) => typeof n === 'number' && n > 0)
  return kept.length ? Object.fromEntries(kept) : undefined
}

/**
 * The one event a click is sent as, once it is known what it led to, and what the page
 * view it led to says about it. An answer carries the click; a click that only changed
 * the page is told by that page view (`via…`) and sends nothing of its own; any other
 * is a `Component Clicked` whose `outcome` says whether anything could react to it.
 * Whichever carries the click carries its `suppressed` counts.
 */
export function settledClick(click: {
  target: ClickTarget
  answers: Record<string, unknown>[]
  clickName?: string
  msSincePageShow: number
  navigated: boolean
  /** Within the page's click cap. */
  countable: boolean
  suppressed?: Record<string, number>
}): {
  event: { name: string; properties: Record<string, unknown> } | null
  via: Record<string, unknown>
} {
  const shared = {
    clickName: click.clickName,
    msSincePageShow: click.msSincePageShow,
    suppressed: click.suppressed,
  }
  const event = click.answers.length
    ? {
        name: 'Answer Given',
        properties: {
          answers: click.answers,
          ...clickFields(click.target),
          outcome: 'answer',
          ...shared,
        },
      }
    : !click.navigated && click.countable
      ? {
          name: 'Component Clicked',
          properties: {
            ...clickFields(click.target),
            outcome: click.target.element ? 'control' : 'none',
            ...shared,
          },
        }
      : null
  if (!click.navigated) return { event, via: {} }
  const handler = click.target.handlerId ?? click.target.componentId
  return {
    event,
    via: prune({
      viaComponentId: handler,
      viaComponentType:
        handler === click.target.componentId ? click.target.componentType : undefined,
      viaPartId: click.target.partId,
      viaElementText: click.target.text,
      viaClickName: click.clickName,
      // Its click is already counted as the answer it gave.
      viaAnswer: click.answers.length > 0 || undefined,
      suppressed: click.answers.length ? undefined : click.suppressed,
    }),
  }
}

type Step = (client: Analytics) => Promise<void>

// The promise resolves once the client has stored the event, which on a device that has
// never read the CDP's settings document waits up to a second for it. Awaited, every later
// event would wait here too, and be read with what the visitor did in the meantime.
const handOff = (sent: Promise<void>): void => {
  sent.catch((err: unknown) => console.warn('[CDP] event not sent', err))
}

// A drag that selects text, or a double-click on a word, ends in a click on that text; a
// selection made elsewhere does not touch the element clicked.
function selectsText(el: ClickElement): boolean {
  try {
    const selection = window.getSelection()
    if (!selection || selection.isCollapsed) return false
    const node = el as unknown as Node
    return node.contains(selection.anchorNode) || node.contains(selection.focusNode)
  } catch {
    return false
  }
}

const cdp: PipeIntegration = (): PipeIntegrationDef => {
  let pipeRef: PipeInterface | null = null
  let state: CdpState | null = null
  let storageKey = ''
  let clientPromise: Promise<Analytics | null> = Promise.resolve(null)
  let chain: Promise<void> = Promise.resolve()
  let waiting = 0
  let consentGated = false
  let currentPage: CurrentPage | null = null
  let clicksOnPage = 0
  let clickTracking = true
  let gesture: Gesture | null = null
  let press: Press | null = null
  // Clicks waiting to learn what they led to, oldest first: usually one, but a click whose
  // actions are still running (a submit waiting on the API) stays while later ones settle.
  let clicks: PendingClick[] = []
  const runs = new Map<string, ActionRun>()
  // Answers given without a click (typed, dragged, keyed), sent together once settled.
  const looseAnswers = new Map<string, AnswerDraft>()
  let looseTimer: ReturnType<typeof setTimeout> | undefined
  let actionsFor: { content: unknown; actions: ComponentActions } | null = null
  let constantsSent = false
  let excluded: ReadonlySet<string> = EXCLUDED_VARIABLES
  // One per document load: a reload or a returning visitor starts a new one, while the
  // run (in localStorage) carries on.
  const pageLoadId = randomId()
  let viewsThisLoad = 0
  let timeZone: string | undefined
  // Read at the first event and again after the page was hidden, the only time the visitor
  // can have changed it: in the device's settings.
  let textScale: { value: number | undefined } | null = null

  const get = (key: string): unknown => pipeRef?.getData(key)
  const textScaleNow = (): number | undefined => (textScale ??= { value: readTextScale() }).value

  // Read from the release content once, and again only if the content object changes.
  function componentActions(): ComponentActions {
    const content = pipeRef?.data.value?.latestContent?.content
    if (!actionsFor || actionsFor.content !== content) {
      actionsFor = { content, actions: indexComponentActions(content) }
    }
    return actionsFor.actions
  }

  function persist(): void {
    if (state && storageKey) safeWrite(storageKey, JSON.stringify(state))
  }

  // One queue for every event, so a page view that waits for `whenReady()` is still
  // sent before the clicks made on that page.
  function enqueue(step: Step): void {
    if (consentGated && waiting >= MAX_EVENTS_WAITING_FOR_CONSENT) return
    waiting++
    chain = chain
      .then(async () => {
        const client = await clientPromise
        if (client) await step(client)
      })
      .catch((err: unknown) => console.warn('[CDP] event not sent', err))
      .finally(() => {
        waiting--
      })
  }

  function pageContext(page: NavigationEventPayload | null): Record<string, unknown> {
    const pageData = page ? pipeRef?.getPageById(page.pageId) : null
    return prune({
      pageId: page?.pageId,
      pagePath: page?.path,
      funnelMarkers: pageData?.funnelMarkers?.length ? pageData.funnelMarkers : undefined,
    })
  }

  // Read when the event is sent, so values the visitor has just entered are included.
  function baseProperties(page: NavigationEventPayload | null): Record<string, unknown> {
    const run = state?.run
    if (run) run.seq++
    const tracking = get('var.global.trackingProperties') as
      | { queryParams?: Record<string, unknown> }
      | undefined
    const currency = get('const.system.currency')
    return prune({
      schemaVersion: CDP_SCHEMA_VERSION,
      pipeId: get('const.system.pipeId') ?? undefined,
      distributionId: get('const.system.distributionId') ?? undefined,
      // `system.buildId` is the release row's id (PipeFullReleaseDto.buildId).
      releaseId: get('const.system.buildId') ?? undefined,
      distributionPath: str(get('const.system.distributionPath')),
      brand: str(get('const.system.brandCode')),
      lang: str(get('const.system.language')),
      geo: str(get('const.system.geography')),
      currency: typeof currency === 'string' ? currency.toUpperCase() : undefined,
      appEnv: str(get('const.system.appEnv')),
      ingressId: state?.ingress.ingressId,
      ingressRuleSetId: state?.ingress.ruleSetId,
      ingressDestinationId: state?.ingress.destinationId,
      ingressTraceId: str(get('var.system.currentIngressTraceId')),
      originIngressTraceId: str(get('var.system.originIngressTraceId')),
      runId: run?.id,
      seq: run?.seq,
      pageLoadId,
      ...pageContext(page),
      email: str(get('var.global.email'))?.trim().toLowerCase(),
      visitorCountry: str(get('var.global.geoDevice.current.geo.country')),
      ...pickCampaign(tracking?.queryParams),
      device: deviceProperties(window, timeZone, textScaleNow()),
      vars: snapshotScalars(pipeRef?.variables.value ?? {}, excluded),
    })
  }

  // The CDP remembers the user id and traits and puts them on every later event, so
  // identify runs only when either changes. `var.global.paymentsApiCustomerId` is the id
  // Segment's integration and NNP's backend identify with, so the three agree.
  async function syncIdentity(client: Analytics): Promise<void> {
    const email = str(get('var.global.email'))?.trim().toLowerCase()
    const userId = str(get('var.global.paymentsApiCustomerId'))
    if (!email && !userId) return
    const known = client.user()
    if ((userId ?? null) === (known.id ?? null) && (email ?? null) === (known.traits.email ?? null))
      return
    // Later events need the new identity, which the client holds once it accepts the call
    // (it emits `identify` then), not once the call is sent.
    await new Promise<void>((resolve) => {
      client.once('identify', () => resolve())
      client.identify(userId, email ? { email } : undefined).then(resolve, (err: unknown) => {
        console.warn('[CDP] identify not sent', err)
        resolve()
      })
    })
  }

  // The run state is persisted before the client is awaited: a tab closed mid-send would
  // otherwise lose the new `seq`, and the next load would send it again.
  function sendTrack(event: string, extra: Record<string, unknown>, page = currentPage): void {
    enqueue(async (client) => {
      const properties = { ...baseProperties(page), ...extra }
      persist()
      await syncIdentity(client)
      handOff(client.track(event, properties))
    })
  }

  function onPageShow(payload: NavigationEventPayload): void {
    const shownAt = Date.now()
    const isNewLoad = viewsThisLoad++ === 0
    // The clicks made on the page being left go out now, attributed to it. The one whose
    // actions asked for this page is named by this page view, as the answer it gave or as
    // nothing of its own; any other is sent as what it was.
    const via = settleAllClicks(true)
    flushLooseAnswers()
    gesture = null
    currentPage = { ...payload, shownAt }
    clicksOnPage = 0

    // The run moves on now, not when the event is sent: a second navigation arriving
    // before this one's send must already see this page as the one before it.
    const run = state?.run
    const previous = run?.lastPage
    const seenBefore = run ? run.seenPageIds.includes(payload.pageId) : false
    if (run && !seenBefore) run.seenPageIds.push(payload.pageId)
    if (run) run.lastPage = { id: payload.pageId, shownAt, loadId: pageLoadId }
    const depth = run?.seenPageIds.length
    persist()

    enqueue(async (client) => {
      await pipeRef?.whenReady()
      const content = pipeRef?.data.value?.latestContent?.content as
        | { pages?: { id: string }[]; landingPageId?: string }
        | undefined
      const constants = constantsSent ? undefined : snapshotScalars(pipeRef?.constants.value ?? {})
      constantsSent = true

      const properties = {
        ...baseProperties(payload),
        pageTitle: shortText(payload.title),
        pageIndex: content?.pages?.findIndex((p) => p.id === payload.pageId),
        isLanding: content?.landingPageId === payload.pageId,
        isFirstView: !seenBefore,
        navType: navType(previous?.id, payload.pageId, seenBefore),
        isNewLoad,
        depth,
        previousPageId: previous?.id,
        // Across a reload or a return visit the gap is time away, not time on the page.
        previousPageDurationMs:
          previous && previous.loadId === pageLoadId ? shownAt - previous.shownAt : undefined,
        ...via,
        consts: constants,
      }
      persist()
      await syncIdentity(client)
      handOff(client.page(payload.path, properties))
    })
  }

  const latestClick = (): PendingClick | undefined => clicks[clicks.length - 1]

  // Whether an event belongs to the latest click: a native radio or checkbox writes on the
  // `change` that follows its click, and a label clicks its input again.
  function partOfPendingClick(target: ClickElement, within: number): PendingClick | undefined {
    const click = latestClick()
    if (!click || click.page !== currentPage || Date.now() - click.at > within) return undefined
    const chain = componentChain(target).map((c) => c.id)
    return chain.includes(click.target.componentId) ||
      click.target.componentPath.includes(chain[0] ?? '')
      ? click
      : undefined
  }

  function onGesture(event: Event): void {
    if (!currentPage) return
    const target = event.target as ClickElement | null
    if (!target || typeof target.closest !== 'function') return
    gesture = {
      event,
      target,
      at: Date.now(),
      page: currentPage,
      typed: isTypedText(target),
      pointerId: event.type === 'pointerdown' ? (event as PointerEvent).pointerId : undefined,
      click:
        event.type === 'input' || event.type === 'change'
          ? partOfPendingClick(target, CLICK_SETTLE_MS)
          : undefined,
    }
    if (event.type === 'pointerdown') press = null
    else if (event.type === 'pointerup') {
      press = { up: event, at: gesture.at, page: currentPage, clicked: false }
    }
  }

  // A touch the browser takes over for scrolling or zooming ends in `pointercancel` and no
  // click: a swipe is nobody's answer, so nothing written meanwhile is matched to it.
  function onPointerCancel(event: Event): void {
    if (gesture && gesture.pointerId === (event as PointerEvent).pointerId) gesture = null
  }

  function onClick(event: Event): void {
    const el = event.target as ClickElement | null
    if (!el || typeof el.closest !== 'function') return
    // The browser's click for a press whose pointerup a run already counted is that click.
    // Only the first click after the pointerup is the press's own: a label then clicks its
    // input, which is the usual repeat.
    const own = press && !press.clicked && (event as MouseEvent).detail > 0 ? press : undefined
    if (own) own.clicked = true
    if (own?.click) {
      onGesture(event)
      if (gesture && clicks.includes(own.click)) gesture.click = own.click
      return
    }
    const same = partOfPendingClick(el, SAME_CLICK_MS)
    if (same) {
      same.repeats++
      onGesture(event)
      if (gesture) gesture.click = same
      return
    }
    if (!currentPage) return
    const actions = componentActions()
    const target = resolveClickTarget(el, (id) => actions.handlers.has(id))
    // The end of a drag that selected text clicks nothing, and a double-click that
    // selected a word is its first click again.
    if (target && !target.element && selectsText(el)) {
      const latest = latestClick()
      if (latest?.page === currentPage && latest.target.componentId === target.componentId)
        latest.repeats++
      return
    }
    settleEarlierClicks()
    onGesture(event)
    // Clicking into a text field only focuses it; what is typed there is its own answer.
    if (!target || !gesture || gesture.typed) return
    const click = startClick(target, el, gesture.at, currentPage)
    gesture.click = click
    if (own) own.click = click
  }

  function startClick(
    target: ClickTarget,
    el: ClickElement,
    at: number,
    page: CurrentPage,
  ): PendingClick {
    const countable = clickTracking && clicksOnPage < MAX_CLICKS_PER_PAGE_VIEW
    if (countable) clicksOnPage++
    const click: PendingClick = {
      target,
      chain: componentChain(el),
      page,
      at,
      answers: new Map(),
      extraNames: 0,
      repeats: 0,
      countable,
      runs: new Set(),
      navigating: false,
      reacted: false,
      timer: undefined,
    }
    click.timer = setTimeout(() => {
      click.reacted = true
      settleIfDone(click)
    }, CLICK_SETTLE_MS)
    // An answer its gesture gave before it was a click is that click's: a drag on a slider
    // ends in a click on it, and an option chosen from the keyboard writes before it runs
    // its actions.
    for (const draft of [...looseAnswers.values()]) {
      if (draft.typed || !target.componentPath.includes(draft.componentId)) continue
      click.answers.set(draft.key, draft)
      looseAnswers.delete(draft.key)
    }
    if (looseTimer && !looseAnswers.size) clearTimeout(looseTimer)
    clicks.push(click)
    return click
  }

  // The runs a click sets off are those its components' click events start within its
  // window; a shared node's own processors join the run that reemitted to them.
  function onActionStart(start: ActionRunPayload): void {
    const nodeId = start.sourceInstanceKey.split('/').pop() ?? ''
    const click = start.parentActionId
      ? runs.get(start.parentActionId)?.click
      : GESTURE_EVENTS.includes(start.eventName)
        ? clickFor(nodeId, start.trigger)
        : undefined
    runs.set(start.actionId, { eventName: start.eventName, nodeId, click })
    click?.runs.add(start.actionId)
  }

  // The click a run of a click or submit event is part of: the latest on its component, else
  // the press that set it off (`pressTarget`), which becomes a click now.
  function clickFor(nodeId: string, trigger: Event | undefined): PendingClick | undefined {
    const latest = [...clicks]
      .reverse()
      .find((c) => !c.reacted && c.page === currentPage && c.target.componentPath.includes(nodeId))
    if (latest || !currentPage) return latest
    // Handed over by the component, else still being dispatched as the run starts; a timer's
    // run has neither.
    const event = trigger ?? (gesture?.event.eventPhase ? gesture.event : undefined)
    const el = pressTarget(event, nodeId)
    if (!event || !el) return undefined
    const ownPress = press && press.up === event ? press : undefined
    const from = ownPress ?? (gesture && gesture.event === event ? gesture : undefined)
    if (!from || from.page !== currentPage) return undefined
    // Already counted as the click the browser sent for the press.
    if (ownPress?.click) return clicks.includes(ownPress.click) ? ownPress.click : undefined
    const actions = componentActions()
    const target = resolveClickTarget(el, (id) => actions.handlers.has(id))
    if (!target) return undefined
    settleEarlierClicks()
    const click = startClick(target, el, from.at, currentPage)
    if (ownPress) ownPress.click = click
    if (gesture?.event === event) gesture.click = click
    return click
  }

  function onActionEnd({ actionId }: ActionEndPayload): void {
    const click = runs.get(actionId)?.click
    runs.delete(actionId)
    if (!click) return
    click.runs.delete(actionId)
    settleIfDone(click)
  }

  // A page asked for by a click's actions is that click's doing, and so is one asked for
  // directly (a link in rich text) while its click is handled. Browser Back and the
  // actions of a timer or a page never are.
  function onNavigationRequest(request: NavigationRequestPayload): void {
    const latest = latestClick()
    const click = request.actionId
      ? runs.get(request.actionId)?.click
      : latest && !latest.reacted && latest.page === currentPage
        ? latest
        : undefined
    if (click) click.navigating = true
  }

  // Done once its component has had its window, its actions have finished, and nothing it
  // set off is waiting for a page. One whose page is coming is settled by that page view,
  // or by the next click when that navigation never happened.
  function settleIfDone(click: PendingClick): void {
    if (click.reacted && !click.runs.size && !click.navigating) settleClick(click, false)
  }

  // A new click ends the wait of every earlier one whose actions have finished.
  function settleEarlierClicks(): void {
    for (const click of [...clicks]) if (!click.runs.size) settleClick(click, false)
  }

  /** Settles every waiting click; on a page change, the latest that asked for it led there. */
  function settleAllClicks(pageChanged: boolean): Record<string, unknown> {
    const led = pageChanged ? [...clicks].reverse().find((c) => c.navigating) : undefined
    let via: Record<string, unknown> = {}
    for (const click of [...clicks]) {
      const told = settleClick(click, click === led)
      if (click === led) via = told
    }
    return via
  }

  /** Sends a waiting click as the one event it amounts to (`settledClick`). */
  function settleClick(click: PendingClick, navigated: boolean): Record<string, unknown> {
    clicks = clicks.filter((c) => c !== click)
    if (click.timer) clearTimeout(click.timer)
    // What its runs still do from here on is no longer this click's.
    for (const id of click.runs) {
      const run = runs.get(id)
      if (run) run.click = undefined
    }
    const { answers, writes } = encodeAnswers([...click.answers.values()])
    const { event, via } = settledClick({
      target: click.target,
      answers,
      clickName: click.clickName,
      msSincePageShow: click.at - click.page.shownAt,
      navigated,
      countable: click.countable,
      suppressed: suppressedCounts({
        clicks: click.repeats,
        writes,
        trackedClicks: click.extraNames,
      }),
    })
    if (event) sendTrack(event.name, event.properties, click.page)
    return via
  }

  // A write is the visitor's answer only when it follows their gesture on this page view
  // and comes from what they touched; everything else is the pipe keeping its own state.
  function onVariableSet(write: VariableSetPayload): void {
    if (!currentPage) return
    const run = write.actionId ? runs.get(write.actionId) : undefined
    // What a click's actions write is that click's answer, however long they took.
    if (run?.click) {
      if (!isAnswerKey(write.key, excluded)) return
      const earlier = run.click.answers.get(write.key)
      run.click.answers.set(write.key, {
        key: write.key,
        value: write.value,
        typed: false,
        source: 'action',
        componentId: run.nodeId,
        componentType: run.click.chain.find((c) => c.id === run.nodeId)?.type,
        at: Date.now(),
        writes: (earlier?.writes ?? 0) + 1,
      })
      return
    }
    // A timer's or a page's own actions keep the pipe's state, whatever was touched last.
    if (run && !GESTURE_EVENTS.includes(run.eventName)) return
    // A drag or typing still going on keeps updating the answer it started.
    const ongoing = latestClick()?.answers.get(write.key) ?? looseAnswers.get(write.key)
    if (ongoing) {
      ongoing.value = write.value
      ongoing.at = Date.now()
      ongoing.writes++
      if (looseAnswers.get(write.key) === ongoing) settleLoose()
      return
    }
    const g = gesture
    if (!g || g.page !== currentPage || Date.now() - g.at > GESTURE_WINDOW_MS) return
    if (!isAnswerKey(write.key, excluded)) return
    const writer = answerWriter(write, componentChain(g.target), componentActions())
    if (!writer) return
    const draft: AnswerDraft = {
      key: write.key,
      value: write.value,
      typed: g.typed,
      ...writer,
      at: Date.now(),
      writes: 1,
    }
    if (g.click && clicks.includes(g.click)) {
      g.click.answers.set(write.key, draft)
    } else {
      looseAnswers.set(write.key, draft)
      settleLoose()
    }
  }

  // Typed text is sent when the field loses focus or the page changes, so a pause in
  // typing is not a changed answer; a drag once it stops moving.
  function settleLoose(): void {
    if (looseTimer) clearTimeout(looseTimer)
    const onlyTyped = [...looseAnswers.values()].every((a) => a.typed)
    looseTimer = onlyTyped ? undefined : setTimeout(() => flushLooseAnswers(), ANSWER_SETTLE_MS)
  }

  /** Every answer settled without a click, as one event. */
  function flushLooseAnswers(onlyTyped = false): void {
    const drafts = [...looseAnswers.values()].filter((a) => !onlyTyped || a.typed)
    if (!drafts.length || !currentPage) return
    for (const d of drafts) looseAnswers.delete(d.key)
    if (looseTimer && !looseAnswers.size) clearTimeout(looseTimer)
    const { answers, writes } = encodeAnswers(drafts)
    if (answers.length) {
      sendTrack('Answer Given', {
        answers,
        msSincePageShow: Math.min(...drafts.map((d) => d.at)) - currentPage.shownAt,
        suppressed: suppressedCounts({ writes }),
      })
    }
  }

  /**
   * Answers as sent, each counted against the run so a changed one is told apart, and the
   * writes folded into them beyond one each.
   */
  function encodeAnswers(drafts: AnswerDraft[]): {
    answers: Record<string, unknown>[]
    writes: number
  } {
    const run = state?.run
    if (!run) return { answers: [], writes: 0 }
    const out: Record<string, unknown>[] = []
    let writes = 0
    for (const d of drafts) {
      const encoded = answerPayload(d.value, d.typed)
      if (!encoded) continue
      writes += d.writes - 1
      const answered = (run.answered ??= {})
      if (d.key in answered || Object.keys(answered).length < MAX_ANSWERED_KEYS) {
        answered[d.key] = (answered[d.key] ?? 0) + 1
      }
      out.push(
        prune({
          key: d.key,
          values: encoded.answerValues,
          kind: encoded.answerKind,
          source: d.source,
          revision: answered[d.key],
          componentId: d.componentId,
          componentType: d.componentType,
        }),
      )
    }
    if (out.length) persist()
    return { answers: out, writes }
  }

  // Consent can only be read after `setup` has run (the decision lives in its own
  // localStorage key), so the gate resolves asynchronously and events queue behind it.
  async function consentGranted(pipe: PipeInterface): Promise<boolean> {
    if (get('const.global.cookieConsentEnabled') !== true) return true
    const { readStoredConsent } = await import('@render/lib/cookieConsent')
    const stored = readStoredConsent()
    if (stored !== 'undecided') return stored === 'granted'
    return new Promise((resolve) => {
      const off = pipe.subscribe('cookie-consent:changed', (decision: 'granted' | 'denied') => {
        off()
        resolve(decision === 'granted')
      })
    })
  }

  async function loadClient(config: CdpConfig, host: string, gate: Promise<boolean>) {
    if (!(await gate)) return null
    try {
      const { load } = await import('@cppv3/cdp-browser')
      const client = await load(
        { writeKey: config.writeKey as string, host },
        { highEntropyValuesClientHints: CLIENT_HINTS },
      )
      if (config.debug) client.debug(true)
      return client
    } catch (err) {
      console.warn('[CDP] client failed to load; no events will be sent from this page', err)
      return null
    }
  }

  return {
    setup(pipe: PipeInterface, config: CdpConfig) {
      if (typeof window === 'undefined') return
      // Editor previews carry `__editor__` ids and would pollute the warehouse.
      if (pipe.currentMode.value !== STATIC_MODE) return
      if (pipe.getData('const.system.distributionId') === '__editor__') return

      const appEnv = pipe.getData('const.system.appEnv') as string | undefined
      const host = config?.host?.trim() || CDP_HOSTS[appEnv ?? 'development']
      if (!config?.writeKey || !host) {
        console.warn('[CDP] missing write key or host; the integration is inactive')
        return
      }
      pipeRef = pipe
      excluded = new Set([...EXCLUDED_VARIABLES, ...(config.excludeVariables ?? [])])
      timeZone = pageTimeZone()

      const distributionPath =
        (pipe.getData('const.system.distributionPath') as string) || 'unknown'
      storageKey = `cdp_state_${distributionPath}`
      state = parseState(safeRead(storageKey), randomId, Date.now())
      const landing = readIngressIds(window.location.search)
      if (landing.ingressId || landing.destinationId) state.ingress = landing
      persist()

      consentGated = config.honourCookieConsent === true
      const gate = consentGated ? consentGranted(pipe) : Promise.resolve(true)
      clientPromise = loadClient(config, host, gate)

      // The landing-page reset fires before the landing page's own `navigation:show`, so
      // the new run includes the page that started it.
      pipe.subscribe('pipe:data-cleared', () => {
        if (!state) return
        state.run = newRun(randomId(), Date.now())
        persist()
      })
      pipe.subscribe(VARIABLE_SET_EVENT, onVariableSet)
      pipe.subscribe(ACTION_START_EVENT, onActionStart)
      pipe.subscribe(ACTION_END_EVENT, onActionEnd)
      pipe.subscribe(NAVIGATION_REQUEST_EVENT, onNavigationRequest)
      pipe.subscribe('navigation:show', onPageShow)
      pipe.subscribe('navigation:exit-intent', (payload: NavigationEventPayload) => {
        sendTrack('Exit Intent Triggered', { exitIntentTrigger: payload.trigger ?? null })
      })

      for (const { channel, event } of PAYMENT_LIFECYCLE) {
        pipe.subscribe(channel, (payload: PaymentEventPayload) => {
          const extra = pickPaymentFields(payload)
          if (
            channel ===
            ('payment:checkout-completed' satisfies PaymentChannels['checkoutCompleted'])
          ) {
            // Cumulative totals, already updated by recordPurchase when this runs (GTM
            // reads them at the same point).
            extra.purchasedOfferSum = num(get('var.global.purchasedOfferSum'))
            extra.purchasedOfferTypes = get('var.global.purchasedOfferTypes') ?? undefined
          }
          sendTrack(event, extra)
        })
      }

      pipe.subscribe(ORDER_CREATED_EVENT, (payload: OrderCreatedPayload) => {
        const lead = leadProperties(payload)
        if (lead) sendTrack(LEAD_CREATED, lead)
      })

      // An author-named click rides on the event its click is sent as; one fired by any
      // other run (a page-show action, a key press) is sent on its own.
      pipe.subscribe(CUSTOM_EVENT, (payload: { eventName?: string; actionId?: string }) => {
        const name = str(payload?.eventName)?.slice(0, MAX_STRING_LENGTH)
        if (!name) return
        const click = payload?.actionId ? runs.get(payload.actionId)?.click : undefined
        if (click) {
          if (click.clickName) click.extraNames++
          else click.clickName = name
          return
        }
        sendTrack('Tracked Click', { clickName: name })
      })

      pipe.subscribe('cookie-consent:changed', (decision: string) => {
        sendTrack('Cookie Consent Changed', { decision })
      })

      // A different email means a different person may be at this device: drop the old
      // user id and traits, but keep the device's anonymous id so the funnel run is not
      // split in two. The next event identifies the new address.
      pipe.subscribe(RESET_USER_EVENT, () => {
        enqueue(async (client) => {
          const { anonymousId } = client.user()
          await client.reset()
          await client.setAnonymousId(anonymousId)
        })
      })

      // Gestures are watched even without click tracking: an answer is matched to one.
      clickTracking = !config.disableClickTracking
      document.addEventListener('click', onClick, { capture: true, passive: true })
      for (const type of ['input', 'change', 'keydown', 'pointerdown', 'pointerup']) {
        document.addEventListener(type, onGesture, { capture: true, passive: true })
      }
      document.addEventListener('pointercancel', onPointerCancel, { capture: true, passive: true })
      document.addEventListener('focusout', () => flushLooseAnswers(true), { capture: true })
      // A hidden tab may never come back, so what is still settling goes now — a click
      // whose submit is still waiting on the API included, as a click that led nowhere.
      // The client sends it kept alive only if it gets there before the page is frozen,
      // so nothing between here and `client.track` may wait on a timer.
      const leaving = () => {
        settleAllClicks(false)
        flushLooseAnswers()
      }
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') leaving()
        else textScale = null
      })
      window.addEventListener('pagehide', leaving)
    },
  }
}

export default cdp
